import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Button } from "@/components/button";
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  PrinterIcon,
} from "@heroicons/react/20/solid";
import { useQueryGetExamPaper } from "@/apis/exam_papers/getExamPaper";
import { useDeleteExamPaper } from "@/apis/exam_papers/deleteExamPaper";
import { apiGetScoreConversionTable } from "@/apis/score_conversion_tables";
import ExamPaperDetails from "./components/ExamPaperDetails";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
  DropdownDivider,
} from "@/components/dropdown";

const ExamPaperDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [deleteError, setDeleteError] = useState<string | null>(null);

  if (!id) {
    return (
      <PageContainer>
        <div className="p-8 text-center text-red-600">
          <Text>Invalid exam paper ID.</Text>
          <Button onClick={() => navigate("/exam_papers")} className="mt-4">
            Back to List
          </Button>
        </div>
      </PageContainer>
    );
  }

  // Query exam paper data
  const { data: examPaper, isLoading, error } = useQueryGetExamPaper(id);

  // Delete exam paper mutation
  const { mutate: deleteExamPaper, isPending: isDeleting } =
    useDeleteExamPaper();

  // Go back to list
  const handleBack = () => {
    navigate("/exam_papers");
  };

  // Go to edit page
  const handleEdit = () => {
    navigate(`/exam_papers/${id}/edit`);
  };

  // Go to print page
  const handlePrintQuestions = () => {
    navigate(`/exam_papers/${id}/print/questions`);
  };

  // Handle delete
  const handleDelete = () => {
    if (!examPaper || isDeleting) return;

    if (
      window.confirm(`Are you sure you want to delete "${examPaper.title}"?`)
    ) {
      setDeleteError(null);

      deleteExamPaper(id, {
        onSuccess: () => {
          // Show success message if needed
          // Then navigate to the exam paper list
          navigate("/exam_papers");
        },
        onError: (error: any) => {
          // Show error message
          setDeleteError(error?.message || "Failed to delete exam paper");
        },
      });
    }
  };

  // Handle Score Table navigation
  const handleScoreTableClick = async () => {
    if (!examPaper?.score_conversion_table_id) {
      // 점수표가 연결되지 않은 경우 생성 페이지로 이동
      navigate(`/score_conversion_tables/create?exam_paper_id=${id}`);
      return;
    }

    try {
      // 실제로 점수표가 존재하는지 확인
      const response = await apiGetScoreConversionTable(examPaper.score_conversion_table_id);
      if (response) {
        // 점수표가 존재하면 상세 페이지로 이동
        navigate(`/score_conversion_tables/${examPaper.score_conversion_table_id}`);
      }
    } catch (error) {
      // 점수표가 존재하지 않으면 (404 등) 생성 페이지로 이동
      navigate(`/score_conversion_tables/create?exam_paper_id=${id}`);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <PageContainer>
        <div className="p-8 text-center">
          <Text>Loading exam paper information...</Text>
        </div>
      </PageContainer>
    );
  }

  // Error state
  if (error || !examPaper) {
    return (
      <PageContainer>
        <div className="p-8 text-center text-red-600">
          <Text>Failed to load exam paper information. Please try again.</Text>
          <Button onClick={handleBack} className="mt-4">
            Back to List
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        {deleteError && (
          <div className="p-4 bg-red-50 text-red-600 rounded-md">
            {deleteError}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button plain onClick={handleBack}>
              <ArrowLeftIcon className="h-5 w-5" />
              Back
            </Button>

            <div>
              <Heading>Exam Paper Details: {examPaper.title}</Heading>
              <Text className="text-zinc-500">
                View complete information about this exam paper.
              </Text>
            </div>
          </div>

          <div className="flex gap-2">
            {/* Show Score Table button for ACT exams */}
            {examPaper.exam_type === "ACT" && (
              <Button 
                onClick={handleScoreTableClick}
                outline
              >
                Score Table
              </Button>
            )}
            
            <Button onClick={handleEdit} color="white">
              <PencilIcon className="h-4 w-4" />
              Edit
            </Button>

            <Dropdown>
              <DropdownButton color="white" aria-label="More options">
                <EllipsisVerticalIcon className="h-4 w-4" />
              </DropdownButton>

              <DropdownMenu>
                <DropdownItem onClick={handlePrintQuestions}>
                  Print Questions
                </DropdownItem>

                <DropdownDivider />

                <DropdownItem
                  danger
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>

        {/* Exam Paper Details Component */}
        <div className="p-8 border border-gray-200 rounded-md bg-white">
          <ExamPaperDetails examPaper={examPaper} />
        </div>
      </div>
    </PageContainer>
  );
};

export default ExamPaperDetailPage;
